<template>
  <div id="activatedTable" class="data-card card">
    <div class="data-container">
      <div class="button-header">
        <Button class="icon icon-left outline-white light-text" :label="$t('app.campaign.follow.headerButtons.addTalent')" :disabled="campaign.archived" @click="goToNewTalent">
          <template #prefix>
            <PlusIcon style="width: 16px;"></PlusIcon>
          </template>
        </Button>
        <Button :disabled="!canActivateSelection" class="icon icon-left outline-white light-text" :label="$t('app.campaign.follow.headerButtons.activateAssessment')" @click="openActionModal([...state.selectedParticipants], 'activate')">
          <template #prefix>
            <ActivatedButtonIcon></ActivatedButtonIcon>
          </template>
        </Button>
        <Button data-step="10" :disabled="!canDesactivateSelection" class="icon icon-left outline-white light-text" :label="$t('app.campaign.follow.headerButtons.desactivateAssessment')" @click="openActionModal([...state.selectedParticipants], 'desactivate')">
          <template #prefix>
            <DesactivatedButtonIcon></DesactivatedButtonIcon>
          </template>
        </Button>
        <VDropdown :distance="10" :disabled="campaign.archived" placement="right">
          <button class="icon icon-left outline-white light-text" :class="{ disabled: campaign.archived }">
            <ActionsButtonIcon style="height: 16px;"></ActionsButtonIcon>
            <label>{{ $t('app.campaign.follow.headerButtons.actions.label') }}</label>
          </button>
          <template #popper>
            <button :disabled="anySelectedHasStartedAssessment"
              class="action red-color" v-close-popper
              @click="openActionModal([...state.selectedParticipants], 'delete')"
              v-tooltip="anySelectedHasStartedAssessment ? $t('app.campaign.follow.headerButtons.actions.removeTalentTooltip') : null"
            >
              <i class="bi bi-trash" />
              <span>{{ $t("app.campaign.activatedTable.deleteTalent") }}</span>
            </button>
            <button class="action green-color" v-close-popper @click="state.showHelpModal = true">
              <i class="bi bi-question-circle" />
              <span>{{ $t('app.campaign.activatedTable.assistance') }}</span>
            </button>
            <div class="separator" />
            <button :disabled="!noneHasFinished || !allSelectedActivated"
              class="action" v-close-popper @click="sendBulkReminder()"
              v-tooltip="!noneHasFinished || !allSelectedActivated ? $t('app.campaign.follow.headerButtons.actions.remindTalentTooltip') : null"
            >
              <i class="bi bi-arrow-counterclockwise" />
              <span>{{ $t('app.campaign.activatedTable.remindTalent') }}</span>
            </button>
            <button :disabled="anySelectedHasStartedAssessment"
              class="action" v-close-popper @click="openActionModal([...state.selectedParticipants], 'move')"
              v-tooltip="anySelectedHasStartedAssessment ? $t('app.campaign.follow.headerButtons.actions.moveTalentTooltip') : null"
            >
              <i class="bi bi-arrow-left-right" />
              <span>{{ $t("app.campaign.activatedTable.moveTalent") }}</span>
            </button>
          </template>
        </VDropdown>
        <VDropdown class="put-to-end" :distance="10" :disabled="campaign.archived" placement="left">
          <button class="icon icon-left outline-white light-text">
            <FilterButtonIcon style="width: 16px;"></FilterButtonIcon>
            <label>{{ $t('app.campaign.follow.headerButtons.filter.label') }}</label>
          </button>
          <template #popper>
            <div class="action">
              <input id="checkbox-1" v-model="state.filters.notActivated" type="checkbox">{{ $t('app.campaign.follow.headerButtons.filter.notActivated') }}</input>
            </div>
            <div class="action">
              <input id="checkbox-2" v-model="state.filters.accountNotCreated" type="checkbox">{{ $t('app.campaign.follow.headerButtons.filter.accountNotCreated') }}</input>
            </div>
            <div class="action">
              <input id="checkbox-3" v-model="state.filters.pendingStart" type="checkbox">{{ $t('app.campaign.follow.headerButtons.filter.pendingStart') }}</input>
            </div>
            <div class="action">
              <input id="checkbox-4" v-model="state.filters.inProgress" type="checkbox">{{ $t('app.campaign.follow.headerButtons.filter.inProgress') }}</input>
            </div>
            <div class="action">
              <input id="checkbox-5" v-model="state.filters.finished" type="checkbox">{{ $t('app.campaign.follow.headerButtons.filter.availableResults') }}</input>
            </div>
          </template>
        </VDropdown>
        <VField class="light" :placeholder="$t('app.campaign.single.search')" name="search" icon="bi-search" v-model="state.search.value" @input="(event) => (state.search.value = event.target.value)" />
      </div>
      <div data-step="9" class="tracking-table">
        <Table
          :data="filteredParticipants"
          :fields="tabFields"
          :search="state.search"
          :actions="false"
          :bulk="{ actions: [] }"
          :removeBulkActions="true"
          :itemsPerPage="100"
          :rowStyleOnCondition="(row) => {
            if (row.state === 2) {
              return {
                'background-color': isDark ? '#0F0E0E' : '#F2F2F2',
              };
            } else return undefined;
          }"
          :rowClick="false"
          :rowClickToSelect="true"
          @selectedRow="updateSelectedRows"
        >
          <template #[`candidate.last_name`]="{ data }">
            <span v-if="data.candidate_id">{{ data.candidate.first_name }} {{ data.candidate.last_name }} </span>
            <span v-else>
              <p v-if="!data.pending_first_name || !data.pending_last_name" class="muted">
                {{ $t("app.campaign.activatedTable.notProvided") }}
              </p>
              <template v-else> {{ data.pending_first_name }} {{ data.pending_last_name }} </template>
            </span>
          </template>
          <template #[`candidate.email`]="{ data }">
            <span v-if="data.candidate_id" v-text="data.candidate.email" />
            <span v-else v-text="data.pending_email" />
          </template>
          <template #creation_time="{ data }">
            <div
              :style="{ width: 'fit-content' }"
              v-text="getDate(data.creation_time, 'LL')"
              v-tooltip="{
                content: getDate(data.creation_time, 'LLLL'),
                placement: 'top',
              }"
            />
          </template>
          <template #progress="{ data }">
            <div class="container center-field">
              <div v-if="data.state === 0 && !data.activated" class="tag black" v-text="$t('app.campaign.follow.headerButtons.filter.notActivated')" v-tooltip="$t('app.campaign.follow.notActivated.tooltip')" />
              <div v-else-if="data.state === 0 && !data.candidate" class="tag grey-light" v-text="$t('app.campaign.follow.headerButtons.filter.accountNotCreated')" v-tooltip="$t('app.campaign.follow.accountNotCreated.tooltip')" />
              <div v-else-if="data.state === 0 && data.candidate" class="tag orange" v-text="$t('app.campaign.follow.headerButtons.filter.pendingStart')" v-tooltip="$t('app.campaign.follow.pendingStart.tooltip')" />
              <div v-else-if="data.state === 1" class="progress-bar-container" v-tooltip="$t('app.campaign.follow.inProgress.tooltip')">
                <span>{{ data.progress || 0 }}%</span>
                <div class="bar">
                  <div class="progress" :style="{ width: `${data.progress || 0}%` }" />
                </div>
              </div>
              <div v-else-if="data.state !== 2" class="tag orange" v-text="$t('app.campaign.follow.calculatingResults.label')" v-tooltip="$t('app.campaign.follow.calculatingResults.tooltip')" />
              <div v-else-if="data.state === 2" class="tag green" v-text="$t('app.campaign.follow.headerButtons.filter.availableResults')" />
            </div>
          </template>
          <template #empty>
            <Empty
              :message="$t('app.campaign.follow.empty')"
              :dark="isDark"
              icon="box"
            />
          </template>
        </Table>
      </div>
      <ActionModal :modal="state.actionModal.modal" :data="state.participants" :isOpen="state.actionModal.show" @close="state.actionModal.show = false" @confirm="state.actionModal.show = false" />
      <HelpModal :isOpen="state.showHelpModal" @close="state.showHelpModal = !state.showHelpModal" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.tracking-table {
  width: 100%;
  height: 100%;
  margin-top: 16px;
}

.center-field {
  justify-content: center !important;
  padding-top: 10px;
  padding-bottom: 10px;
}

.button-header {
  display: flex;
  flex-direction: row;
  gap: 8px;

  .put-to-end {
    margin-left: auto;
  }
}

.progress-bar-container .bar .progress {
  background-color: $blue-color;
}
</style>

<script setup lang="ts">
import { reactive, inject } from "vue";
import { storeToRefs } from "pinia";
import { useAppStore } from "@/stores/app.js";
import { useOnboardingStore } from "@/stores/onboarding.js";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { useDark } from "@vueuse/core";
import { getDate, dateIsBefore } from "@/composables/utils";
import Empty from "@/components/table/Empty.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import HelpModal from "@/components/modals/app/helpModal/HelpModal.vue";

import ActivatedButtonIcon from '@/assets/images/icon/campaign/follow/activate.svg?component';
import DesactivatedButtonIcon from '@/assets/images/icon/campaign/follow/desactivate.svg?component';
import ActionsButtonIcon from '@/assets/images/icon/campaign/action.svg?component';
import FilterButtonIcon from '@/assets/images/icon/campaign/filter.svg?component';
import PlusIcon from '@/assets/images/icon/campaign/plus.svg?component';

import { useParticipations, useRemindParticipation } from "@/queries/participant.query";
import { useCampaign } from "@/queries/campaigns.query";

const isDark = useDark();
const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const { t } = useI18n();

const moment = inject("moment");

const { data: participants, isPending } = useParticipations(appStore.onboardingIsLive);
const { mutate: remindParticipation } = useRemindParticipation();
const { data: campaign, isPending: isCampaignPending } = useCampaign(appStore.onboardingIsLive);

const toast = useToast();

defineProps({
  search: {
    type: Object,
  },
});

const state = reactive({
  selectedParticipants: [],
  filters: {
    notActivated: false,
    accountNotCreated: false,
    pendingStart: false,
    inProgress: false,
    finished: false,
  },
  actionModal: {
    show: false,
    modal: "",
  },
  search: {
    value: "",
    searchable: [
      {
        combined: true,
        keys: ["pending_first_name", "pending_last_name"],
      },
      "pending_email",
      {
        combined: true,
        keys: ["candidate.first_name", "candidate.last_name"],
      },
      "candidate.email",
    ],
  },
  showHelpModal: false,
  participants: [],
});

const tabFields = computed(() => {
  return [
    {
      id: "candidate.last_name",
      fallback: "pending_last_name",
      label: "Talent",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "candidate.email",
      fallback: "pending_email",
      label: t("app.campaign.activatedTable.email"),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "creation_time",
      label: t("app.campaign.activatedTable.addedDate"),
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b, a) ? 1 : -1;
        } else {
          return dateIsBefore(a, b) ? 1 : -1;
        }
      },
    },
    {
      id: "progress",
      label: t("app.campaign.activatedTable.progress"),
      defaultSortDir: "DESC",
      multipleSort: true,
      center: true,
      sort(a, b, dir) {
        if (dir === "ASC") {
          if (b.candidate && !a.candidate) return -1;
          if (!b.candidate && a.candidate) return 1;

          if (b.state > 0 && a.state === 0) return -1;
          if (b.state === 0 && a.state > 0) return 1;
          if (b.state === 2 && a.state === 1) return -1;

          if (a.progress !== b.progress) return a.progress - b.progress;

          return dateIsBefore(a.creation_time, b.creation_time) ? 1 : -1;
        } else {
          if (a.state === 2 )
          if (a.candidate && !b.candidate) return -1;
          if (!a.candidate && b.candidate) return 1;

          if (a.state > 0 && b.state === 0) return -1;
          if (a.state === 0 && b.state > 0) return 1;
          if (a.state === 2 && b.state === 1) return -1;

          if (a.progress !== b.progress) return b.progress - a.progress;

          return dateIsBefore(a.creation_time, b.creation_time) ? 1 : -1;
        }
      },
    },
  ];
});

function openActionModal(participants, modal) {
  state.participants = participants;
  state.actionModal = {
    show: true,
    modal,
  };
}

async function sendBulkReminder() {
  for (const participant of state.selectedParticipants) {
    sendReminderToParticipant(participant);
  }
}

async function sendReminderToParticipant(participant) {
  const diff = moment().diff(moment.utc(participant.last_reminder_time, "YYYY/MM/DD HH:mm:ss")) / 1000;

  if (!isNaN(diff) && diff < 1800) {
    //if already sent a reminder in the last 30 minutes, don't event send an API call
    toast.info(t("app.campaign.activatedTable.toasts.recentReminder"));

    return;
  }


  remindParticipation({
    context: {
      organisationId: route.params.companyId as string,
      spaceId: route.params.spaceId as string,
      campaignId: route.params.campaignId as string,
    },
    participantId: participant.participant_id,
  }, {
    onSuccess: (reminder) => {
      if (reminder.sent) {
        toast.success(t("app.campaign.activatedTable.toasts.reminderSent"));
      } else {
        toast.info(t("app.campaign.activatedTable.toasts.recentReminder"));
      }
    },
    onError: () => {
      toast.error(t("app.campaign.activatedTable.toasts.reminderSendError"));
    }
  });

}

function updateSelectedRows(rows) {
  state.selectedParticipants = rows;
}

const filteredParticipants = computed(() => {
  if (isPending.value) return [];
  if (!state.filters.notActivated && !state.filters.accountNotCreated
    && !state.filters.pendingStart && !state.filters.inProgress
    && !state.filters.finished) {
    const participantsCopy = [...participants.value];
    return participantsCopy.sort((pA, pB) => {
      const pAState = pA.state;
      const pBState = pB.state;

      if (pAState > pBState) return 1;
      if (pAState < pBState) return -1;
      if (pA.activated && !pB.activated) return 1;
      if (pAState === 1 && pBState === 1) {
        return pA.progress - pB.progress;
      }
      return dateIsBefore(pA.creation_time, pB.creation_time) ? 1 : -1;
    });
  }
  const filtered = participants.value.filter((participant) => {
    if (state.filters.notActivated && !participant.activated) return true;
    if (state.filters.accountNotCreated && participant.state === 0 && !participant.candidate && participant.activated) return true;
    if (state.filters.pendingStart && participant.state === 0 && participant.candidate && participant.activated) return true;
    if (state.filters.inProgress && participant.state === 1) return true;
    if (state.filters.finished && participant.state === 2) return true;
    return false;
  });
  return filtered;
});

const canDesactivateSelection = computed(() => {
  if (state.selectedParticipants.length === 0) return false;
  return state.selectedParticipants.every((participant) => {
    return participant.activated && participant.state === 0;
  });
});

const canActivateSelection = computed(() => {
  if (state.selectedParticipants.length === 0) return false;
  return state.selectedParticipants.every((participant) => {
    return !participant.activated;
  });
});

const noneHasFinished = computed(() => {
  if (state.selectedParticipants.length === 0) return false;
  const someFinished = state.selectedParticipants.some((participant) => {
    return participant.state === 2;
  });
  return !someFinished;
});

const allSelectedActivated = computed(() => {
  if (state.selectedParticipants.length === 0) return false;
  return state.selectedParticipants.every((participant) => {
    return participant.activated;
  });
})

const anySelectedHasStartedAssessment = computed(() => {
  if (state.selectedParticipants.length === 0) return true;
  return state.selectedParticipants.some((participant) => {
    return participant.state === 1 || participant.state === 2;
  });
});

function goToNewTalent() {
  router.push({
    name: 'addCandidates',
    params: {
      campaignId: route.params.campaignId || '8d9740ed-56ac-4f1f-856f-df9d81f16c52',
    },
  });
}
</script>
